import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  Chip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  CircularProgress,
  Avatar,
  AvatarGroup,
  Grid,
  Paper,
  Tabs,
  Tab,
  Badge,
  Tooltip,
} from '@mui/material';
import {
  Search,
  Add,
  MoreVert,
  Edit,
  Delete,
  Visibility,
  Schedule as ScheduleIcon,
  CheckCircle,
  Warning,
  Cancel,
  LocationOn,
  AccessTime,
  Group,
  Today,
  DateRange,
  Person,
  CalendarToday,
  Build,
  BuildCircle,
  Assignment,
  Map,
  FilterList,
  ArrowBack,
  ArrowForward,
} from '@mui/icons-material';
import SharedCalendar from '../../components/SharedCalendar/SharedCalendar';
import axios from 'axios';
import { mockApi } from '../../services/mockData';

interface ScheduleItem {
  id: string;
  type: 'installation' | 'maintenance' | 'inspection' | 'meeting' | 'training';
  title: string;
  description: string;
  customer: string;
  customerId: string;
  status: 'scheduled' | 'in_progress' | 'completed' | 'cancelled' | 'rescheduled';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  scheduledDate: string;
  startTime: string;
  endTime: string;
  estimatedDuration: number;
  actualDuration: number;
  location: string;
  region: string;
  assignedTo: string;
  team: string[];
  notes: string;
  recurring: boolean;
  recurringPattern?: 'daily' | 'weekly' | 'monthly' | 'yearly';
  weatherDependent: boolean;
  specialRequirements: string[];
  createdAt: string;
  updatedAt: string;
}

interface RegionSchedule {
  region: string;
  color: string;
  schedules: ScheduleItem[];
  teamMembers: string[];
  totalJobs: number;
  completedJobs: number;
  inProgressJobs: number;
}

const Scheduling: React.FC = () => {
  const [scheduleItems, setScheduleItems] = useState<ScheduleItem[]>([]);
  const [regionSchedules, setRegionSchedules] = useState<RegionSchedule[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [regionFilter, setRegionFilter] = useState('');
  const [dateFilter, setDateFilter] = useState('');
  const [viewMode, setViewMode] = useState<'calendar' | 'month'>('calendar');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedItem, setSelectedItem] = useState<ScheduleItem | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [sharedCalendarOpen, setSharedCalendarOpen] = useState(false);
  const navigate = useNavigate();

  const fetchScheduleItems = useCallback(async () => {
    try {
      setLoading(true);
      const schedules = await mockApi.getSchedules();
      
      // Convert schedules to ScheduleItem format
      const scheduleItems: ScheduleItem[] = schedules.map(schedule => ({
        id: schedule._id,
        type: schedule.type === 'install' ? 'installation' : 'maintenance',
        title: schedule.description,
        description: schedule.description,
        customer: schedule.customerName,
        customerId: schedule.customerId,
        location: schedule.location,
        scheduledDate: schedule.scheduledDate,
        startTime: schedule.timeSlot,
        endTime: schedule.timeSlot,
        timeSlot: schedule.timeSlot,
        technician: schedule.technician,
        status: schedule.status === 'completed' ? 'completed' as const : schedule.status === 'in_progress' ? 'in_progress' as const : schedule.status === 'cancelled' ? 'cancelled' as const : schedule.status === 'rescheduled' ? 'rescheduled' as const : 'scheduled' as const,
        priority: schedule.priority === 'urgent' ? 'urgent' as const : schedule.priority === 'high' ? 'high' as const : schedule.priority === 'medium' ? 'medium' as const : 'low' as const,
        notes: schedule.notes,
        duration: 4, // Default duration
        region: schedule.location,
        team: [schedule.technician], // Convert single technician to team array
        assignedTeam: [schedule.technician],
        materials: [],
        createdAt: schedule.scheduledDate,
        updatedAt: schedule.scheduledDate,
        estimatedDuration: 4,
        actualDuration: 4,
        assignedTo: schedule.technician,
        recurring: false,
        recurringPattern: undefined,
        completedAt: schedule.status === 'completed' ? schedule.scheduledDate : null,
        weatherDependent: false,
        specialRequirements: schedule.notes ? [schedule.notes] : [],
      }));
      
      setScheduleItems(scheduleItems);
      
      // Group schedules by office
      const officeMap: { [key: string]: RegionSchedule } = {};
      
      scheduleItems.forEach((schedule: ScheduleItem) => {
        const office = schedule.location || 'Unassigned';
        if (!officeMap[office]) {
          officeMap[office] = {
            region: office,
            color: getOfficeColor(office),
            schedules: [],
            teamMembers: [],
            totalJobs: 0,
            completedJobs: 0,
            inProgressJobs: 0,
          };
        }
        
        const officeData = officeMap[office];
        officeData.schedules.push(schedule);
        officeData.totalJobs++;
        
        if (schedule.status === 'completed') officeData.completedJobs++;
        if (schedule.status === 'in_progress') officeData.inProgressJobs++;
        
        // Add team members
        schedule.team.forEach(member => {
          if (!officeData.teamMembers.includes(member)) {
            officeData.teamMembers.push(member);
          }
        });
      });
      
      setRegionSchedules(Object.values(officeMap));
    } catch (err: any) {
      setError(err.message || 'Failed to fetch schedule items');
    } finally {
      setLoading(false);
    }
  }, [searchTerm, typeFilter, statusFilter, priorityFilter, regionFilter, dateFilter]);

  const getOfficeColor = (office: string): string => {
    const colors = {
      'Denver': '#4CAF50',    // Green
      'Boise': '#2196F3',     // Blue
      'Spokane': '#FF9800',   // Orange
      'Olympia': '#9C27B0',   // Purple
      'Unassigned': '#757575', // Gray
    };
    return colors[office as keyof typeof colors] || '#757575';
  };

  useEffect(() => {
    fetchScheduleItems();
  }, [fetchScheduleItems]);

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, item: ScheduleItem) => {
    setAnchorEl(event.currentTarget);
    setSelectedItem(item);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedItem(null);
  };

  const handleView = () => {
    if (selectedItem) {
      // Navigate to schedule item details
      console.log('View schedule item:', selectedItem.id);
    }
    handleMenuClose();
  };

  const handleEdit = () => {
    if (selectedItem) {
      // Navigate to edit page or open edit dialog
      console.log('Edit schedule item:', selectedItem.id);
    }
    handleMenuClose();
  };

  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
    handleMenuClose();
  };

  const handleDeleteConfirm = async () => {
    if (selectedItem) {
      try {
        await mockApi.deleteSchedule(selectedItem.id);
        setScheduleItems(scheduleItems.filter(item => item.id !== selectedItem.id));
        setDeleteDialogOpen(false);
        fetchScheduleItems(); // Refresh region data
      } catch (err: any) {
        setError(err.response?.data?.message || 'Failed to delete schedule item');
      }
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'scheduled': return 'info';
      case 'in_progress': return 'warning';
      case 'completed': return 'success';
      case 'cancelled': return 'error';
      case 'rescheduled': return 'default';
      default: return 'default';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent': return 'error';
      case 'high': return 'warning';
      case 'medium': return 'info';
      case 'low': return 'success';
      default: return 'default';
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'installation': return <Build />;
      case 'maintenance': return <BuildCircle />;
      case 'inspection': return <Assignment />;
      case 'meeting': return <Group />;
      case 'training': return <ScheduleIcon />;
      default: return <ScheduleIcon />;
    }
  };

  const formatTime = (time: string) => {
    return new Date(`2000-01-01T${time}`).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getTodaysSchedules = () => {
    return scheduleItems.filter(item => 
      item.scheduledDate === selectedDate
    );
  };

  const getSchedulesForDate = (date: Date) => {
    const dateString = date.toISOString().split('T')[0];
    return scheduleItems.filter(item => item.scheduledDate === dateString);
  };

  const getSchedulesForMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    return scheduleItems.filter(item => {
      const itemDate = new Date(item.scheduledDate);
      return itemDate.getFullYear() === year && itemDate.getMonth() === month;
    });
  };

  const getCalendarDays = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - firstDay.getDay());
    
    const days = [];
    for (let i = 0; i < 35; i++) {
      const day = new Date(startDate);
      day.setDate(startDate.getDate() + i);
      days.push(day);
    }
    return days;
  };

  const getSchedulesForDay = (date: Date) => {
    const dateString = date.toISOString().split('T')[0];
    return scheduleItems.filter(item => item.scheduledDate === dateString);
  };

  const handleDateClick = (date: Date) => {
    setSelectedDate(date.toISOString().split('T')[0]);
    setSharedCalendarOpen(true);
  };

  const handleMonthChange = (direction: 'prev' | 'next') => {
    const newDate = new Date(currentDate);
    if (direction === 'prev') {
      newDate.setMonth(newDate.getMonth() - 1);
    } else {
      newDate.setMonth(newDate.getMonth() + 1);
    }
    setCurrentDate(newDate);
  };

  const isToday = (date: Date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const isCurrentMonth = (date: Date) => {
    return date.getMonth() === currentDate.getMonth() && date.getFullYear() === currentDate.getFullYear();
  };

  const handleAddSchedule = () => {
    setSharedCalendarOpen(true);
  };

  const handleCalendarDateSelect = (date: Date) => {
    setSelectedDate(date.toISOString().split('T')[0]);
    setSharedCalendarOpen(false);
    // Here you could navigate to a form or open a dialog to add a new schedule
    console.log('Selected date for new schedule:', date);
  };

  const handleSharedCalendarAdd = (date: Date) => {
    setSelectedDate(date.toISOString().split('T')[0]);
    // Here you could open a form to add a new schedule
    console.log('Add new schedule for:', date);
    // Refresh the schedule items after adding
    fetchScheduleItems();
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ backgroundColor: '#1A1A1A', minHeight: '100vh', p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography 
          variant="h4"
          sx={{
            color: '#D4AF37',
            fontWeight: 700,
          }}
        >
          📅 Scheduling Calendar
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={handleAddSchedule}
          sx={{
            backgroundColor: '#D4AF37',
            color: '#000000',
            '&:hover': {
              backgroundColor: '#B8941F',
            },
          }}
        >
            Add to Schedule
        </Button>
      </Box>

      {error && (
        <Box sx={{ mb: 2 }}>
          <Typography color="error">{error}</Typography>
        </Box>
      )}

      {/* Calendar Header */}
      <Card sx={{ mb: 3, backgroundColor: '#2C2C2C', border: '1px solid #333333' }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h5" sx={{ color: '#D4AF37' }}>
              {currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
            </Typography>
            <Box display="flex" gap={1}>
              <Button
                variant="outlined"
                onClick={() => handleMonthChange('prev')}
                sx={{ color: '#D4AF37', borderColor: '#D4AF37' }}
              >
                <ArrowBack />
              </Button>
              <Button
                variant="outlined"
                onClick={() => setCurrentDate(new Date())}
                sx={{ color: '#D4AF37', borderColor: '#D4AF37' }}
              >
                Today
              </Button>
              <Button
                variant="outlined"
                onClick={() => handleMonthChange('next')}
                sx={{ color: '#D4AF37', borderColor: '#D4AF37' }}
              >
                <ArrowForward />
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Calendar Grid */}
      <Card sx={{ backgroundColor: '#2C2C2C', border: '1px solid #333333' }}>
        <CardContent sx={{ p: 0 }}>
          {/* Day Headers */}
          <Grid container>
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
              <Grid item xs key={day} sx={{ p: 2, textAlign: 'center', borderBottom: '1px solid #333333' }}>
                <Typography variant="subtitle2" sx={{ color: '#D4AF37', fontWeight: 'bold' }}>
                  {day}
                </Typography>
              </Grid>
            ))}
          </Grid>

          {/* Calendar Days - 7x5 Grid */}
          {Array.from({ length: 5 }, (_, weekIndex) => (
            <Grid container key={weekIndex}>
              {getCalendarDays(currentDate).slice(weekIndex * 7, (weekIndex + 1) * 7).map((date, dayIndex) => {
                const globalIndex = weekIndex * 7 + dayIndex;
                const daySchedules = getSchedulesForDay(date);
                const isCurrentMonthDay = isCurrentMonth(date);
                const isTodayDate = isToday(date);
                
                return (
                  <Grid 
                    item 
                    xs 
                    key={globalIndex}
                    sx={{ 
                      minHeight: 120,
                      borderRight: dayIndex < 6 ? '1px solid #333333' : 'none',
                      borderBottom: weekIndex < 4 ? '1px solid #333333' : 'none',
                      p: 1,
                      cursor: 'pointer',
                      backgroundColor: isTodayDate ? 'rgba(212, 175, 55, 0.1)' : 'transparent',
                      '&:hover': {
                        backgroundColor: 'rgba(212, 175, 55, 0.05)',
                      },
                    }}
                    onClick={() => handleDateClick(date)}
                  >
                    <Box display="flex" flexDirection="column" height="100%">
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          color: isCurrentMonthDay ? '#FFFFFF' : '#666666',
                          fontWeight: isTodayDate ? 'bold' : 'normal',
                          mb: 1,
                        }}
                      >
                        {date.getDate()}
                      </Typography>
                      
                      <Box flex={1} display="flex" flexDirection="column" gap={0.5}>
                        {daySchedules.slice(0, 3).map((schedule) => (
                          <Chip
                            key={schedule.id}
                            label={schedule.title}
                            size="small"
                            sx={{
                              fontSize: '0.7rem',
                              height: 20,
                              backgroundColor: getOfficeColor(schedule.location || 'Unassigned'),
                              color: '#FFFFFF',
                              '& .MuiChip-label': {
                                px: 1,
                              },
                            }}
                          />
                        ))}
                        {daySchedules.length > 3 && (
                          <Typography variant="caption" sx={{ color: '#AAAAAA', fontSize: '0.7rem' }}>
                            +{daySchedules.length - 3} more
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </Grid>
                );
              })}
            </Grid>
          ))}
        </CardContent>
      </Card>

      {/* Office Legend */}
      <Card sx={{ mt: 3, backgroundColor: '#2C2C2C', border: '1px solid #333333' }}>
        <CardContent>
          <Typography variant="h6" sx={{ color: '#D4AF37', mb: 2 }}>
            Office Legend
          </Typography>
          <Grid container spacing={2}>
            {['Denver', 'Boise', 'Spokane', 'Olympia'].map((office) => (
              <Grid item xs={6} sm={3} key={office}>
                <Box display="flex" alignItems="center" gap={1}>
                  <Box
                    sx={{
                      width: 20,
                      height: 20,
                      backgroundColor: getOfficeColor(office),
                      borderRadius: 1,
                    }}
                  />
                  <Typography variant="body2" sx={{ color: '#FFFFFF' }}>
                    {office}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>

      {/* Shared Calendar Modal */}
      <SharedCalendar
        open={sharedCalendarOpen}
        onClose={() => setSharedCalendarOpen(false)}
        onDateSelect={handleCalendarDateSelect}
        onAddSchedule={handleSharedCalendarAdd}
        selectedDate={new Date(selectedDate)}
        title="Schedule Management"
      />

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleView}>
          <ListItemIcon>
            <Visibility fontSize="small" />
          </ListItemIcon>
          <ListItemText>View Details</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleEdit}>
          <ListItemIcon>
            <Edit fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleDeleteClick}>
          <ListItemIcon>
            <Delete fontSize="small" />
          </ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Schedule Item</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this schedule item? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};

export default Scheduling;